import React from "react";
import useInView from "./useInView";

// The platform section: one campaign, many screens, watched from one place.
//
// --- what replaced what ---------------------------------------------------
//
// This was a hub-and-spoke SVG -- WallBurst in the middle, rays out to four
// screen glyphs -- with a small campaign card floating over it. The diagram
// said "one thing connects to many things", which is a shape, not a product.
// Anyone can draw it and nobody books a screen because of it.
//
// A dashboard says the same thing and also shows what you get: the campaign,
// where it is running, whether it is running, and how far through it is. That
// is the argument.
//
// --- what is deliberately NOT on it ---------------------------------------
//
// No impressions, no audience numbers, no view counts, no engagement rate.
// WallBurst cannot measure any of those -- a screen reports that a slot
// rendered, not who looked at it -- and putting an estimate on a product
// screenshot is how a marketing page starts promising analytics the software
// will never produce.
//
// Everything shown IS something the system genuinely knows: which screens a
// campaign covers, whether each one is playing, how far through the run it is,
// and whether every screen has reported in. That last one is proof-of-play and
// device liveness, which is the honest core of what the platform does.
//
// The figures are invented, so the panel says Demo and the venues are made up.
// A number on a marketing page is read as a metric, and a real business name
// on a dashboard is read as a customer.

const SCREENS = [
    { name: "Downtown Dance Studio", place: "Waiting room", state: "Playing" },
    { name: "Corner Café", place: "Dining area", state: "Playing" },
    { name: "Market Street Gym", place: "Lobby", state: "Playing" },
    { name: "Main Street Nail Salon", place: "Reception area", state: "Playing" },
];

const TAGS = ["Upload once", "Track it live", "Verify playback"];

function ScreenIcon() {
    return (
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <rect x="1" y="2.5" width="14" height="9.5" rx="1.4"
                  fill="none" stroke="currentColor" strokeWidth="1.2" />
            <path d="M5.5 14.2h5" stroke="currentColor" strokeWidth="1.2"
                  strokeLinecap="round" />
        </svg>
    );
}

export default function Network() {
    const [ref, inView] = useInView({ threshold: 0.25 });

    return (
        <section className={"nw" + (inView ? " is-in" : "")} ref={ref}>
            <div className="nw__inner">
                <div className="nw__copy">
                    <p className="nw__eyebrow">The platform</p>
                    <h2 className="nw__title">One campaign.<br />Many screens.</h2>
                    <p className="nw__lede">
                        Upload once. Choose your locations. WallBurst handles the
                        schedule, playback, and reporting across every one of them.
                    </p>
                    {/* Three labels, not three paragraphs. These were full
                        sentences about pull-based scheduling and signed
                        manifests -- all true, none of them a reason anybody
                        books a screen, and together they were taller than the
                        headline they were supporting. */}
                    <ul className="nw__tags">
                        {TAGS.map((tag) => (
                            <li key={tag}>
                                <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
                                    <path d="M1.5 6.3l3 3 6-6.6" fill="none"
                                          stroke="currentColor" strokeWidth="1.8"
                                          strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {tag}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="nw__viz">
                    <div className="nwd" role="img"
                         aria-label="Illustration of a WallBurst campaign dashboard: the
                                     Labor Day Promotion campaign running live on 14 screens
                                     across 8 locations, on day 5 of 9, with every screen
                                     reporting.">
                        <header className="nwd__head">
                            <div>
                                <span className="nwd__kicker">Campaign</span>
                                <span className="nwd__name">Labor Day Promotion</span>
                            </div>
                            <span className="nwd__badge">Demo</span>
                        </header>

                        <dl className="nwd__stats">
                            <div>
                                <dt>Screens</dt>
                                <dd>14</dd>
                            </div>
                            <div>
                                <dt>Locations</dt>
                                <dd>8</dd>
                            </div>
                            <div>
                                <dt>Status</dt>
                                <dd className="nwd__live"><i />Live</dd>
                            </div>
                        </dl>

                        <div className="nwd__progress">
                            <div className="nwd__progress-row">
                                <span>Day 5 of 9</span>
                                <span className="nwd__muted">Ends Sep 12</span>
                            </div>
                            <div className="nwd__bar"><span /></div>
                        </div>

                        <ul className="nwd__screens">
                            {SCREENS.map((screen) => (
                                <li key={screen.name}>
                                    <span className="nwd__icon"><ScreenIcon /></span>
                                    <span className="nwd__screen-main">
                                        <strong>{screen.name}</strong>
                                        <em>{screen.place}</em>
                                    </span>
                                    <span className="nwd__state">
                                        <i />{screen.state}
                                    </span>
                                </li>
                            ))}
                            <li className="nwd__more">+10 more screens</li>
                        </ul>

                        <footer className="nwd__foot">
                            <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                                <path d="M1.5 6.3l3 3 6-6.6" fill="none"
                                      stroke="currentColor" strokeWidth="1.8"
                                      strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            All screens reporting
                        </footer>
                    </div>
                </div>
            </div>
        </section>
    );
}
